import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  input,
  Output,
  ViewChild,
} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeFormServiceModule} from 'se-fe-form-service';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeModalButton, SeFeModalComponent, SeFeModalModule} from 'se-fe-modal';
import {SeFeSpinnerModule} from 'se-fe-spinner';
import {SeFeWysiwygModule} from 'se-fe-wysiwyg';
import {trimFormValue} from '../../../../utils/form-utils';
import {UpdateProductDetail} from '../../../../features/product/components/product-detail/product-detail.store';
import {Product} from '../../../../models/clare';

interface ProductForm {
  name: FormControl<string>;
  price: FormControl<number>;
  description: FormControl<string | null>;
}

@Component({
  selector: 'item-details-modal',
  imports: [
    SeFeAlertModule,
    SeFeButtonModule,
    SeFeFormFieldModule,
    SeFeFormFieldTextModule,
    SeFeFormServiceModule,
    SeFeGridModule,
    SeFeIconModule,
    SeFeModalModule,
    SeFeSpinnerModule,
    SeFeWysiwygModule,
  ],
  templateUrl: './item-details-modal.component.html',
  styleUrls: ['./item-details-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemDetailsModalComponent {
  @Output() save = new EventEmitter<UpdateProductDetail<{product: Product}>>();
  @ViewChild('detailModal') detailModal!: SeFeModalComponent;

  error = input<string | null>();
  product = input<Product>();
  price = input<number>();

  form: FormGroup<ProductForm> = new FormGroup({
    name: new FormControl('', {nonNullable: true, validators: [Validators.required, Validators.maxLength(100)]}),
    price: new FormControl(0, {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'),
        Validators.min(0),
        Validators.max(1_000_000),
      ],
    }),
    description: new FormControl('', {
      validators: [Validators.maxLength(2000)],
    }),
  });

  productNameConfig = {
    label: 'Name',
    errorMessages: {
      required: 'Please enter a product name.',
      maxlength: 'Name exceeds maximum length of 100 characters.',
    },
  };
  productPriceConfig = {
    label: 'Price',
    errorMessages: {
      pattern: 'Please enter a valid price (e.g., $25 or $0.99)',
      required: 'Please enter a price',
      min: 'Price cannot be less than 0',
      max: 'Value exceeds the limit of $1,000,000',
    },
  };
  /**
   * hiding SKU for alpha
   *  itemNumberConfig = {
    label: 'SKU',
    errorMessages: {
    required: 'Please enter a SKU number',
  },
   */
  productDescLabel = {
    label: 'Description',
  };

  detailModalButtons: SeFeModalButton[] = [
    {
      cancel: true,
      label: 'Cancel',
      action: () => {
        this.detailModal.close();
        return 'Cancel';
      },
      loadingLabel: 'Cancel',
    },
    {
      primary: true,
      label: 'Save',
      action: async () => {
        this.handleAction();
        return 'Saving';
      },
      loadingLabel: 'Updating Product',
    },
  ];

  constructor(private cdr: ChangeDetectorRef) {
    /**
     * Sync inputs into the reactive form after the current change-detection turn.
     * Doing this synchronously inside `effect()` caused NG0100 on `SeFeModalButton` (undefined → false)
     * when the modal projected content updated mid-check in dev mode.
     */
    effect(() => {
      const product = this.product();
      const price = this.price();
      queueMicrotask(() => {
        const patch: {name: string; description: string | null; price?: number} = {
          name: product?.name || '',
          description: product?.description ?? '',
        };
        if (price !== undefined) {
          patch.price = price;
        }
        this.form.patchValue(patch);
        this.cdr.markForCheck();
      });
    });
  }

  get descriptionCtrl(): FormControl<string> {
    return this.form.get('description') as FormControl<string>;
  }

  handleAction(): void {
    trimFormValue(this.form, 'name');
    trimFormValue(this.form, 'description');
    this.markFormGroupControls(this.form);
    this.cdr.markForCheck();
    if (!this.form.valid) {
      this.detailModal?.cancelSpinner?.();
      return;
    }
    this.saveProductAndPriceBreak();
  }

  markFormGroupControls(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup) {
        this.markFormGroupControls(control);
      } else {
        control.markAsTouched({onlySelf: true});
        control.markAsDirty({onlySelf: true});
      }
    });
  }

  saveProductAndPriceBreak(): void {
    const product: Partial<Product> = {
      name: this.form.value.name?.trim(),
      description: this.form.value.description || null!,
      price_cents: (this.form.value.price ?? 0) * 100,
    };
    this.save.emit({
      data: {product},
      modal: this.detailModal,
    });
  }

  resetForm(): void {
    this.form.reset({
      name: this.product()?.name,
      price: this.price() ?? 0,
      description: this.product()?.description,
    });
  }
}
